// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Phase-2 analytics & scheduling UI for the Бриф operational screens, kept in a
// separate file so OpsScreens stays a thin composition (no regression risk to
// the existing chat pipeline / screens):
//
//   • PostMetricsPanel — lazy per-post engagement (B1), fetched on demand.
//   • RescheduleSheet  — mobile-first bottom sheet to move a post (B2).
//   • BestTimeHeatmap  — collapsible dow×hour heat grid advisory (B3).

import { useEffect, useState } from 'react';
import { bestTime } from '../promo/analyticsApi';
import { postMetrics, reschedule } from '../promo/postsApi';
import type { BestTimeHeatmap as HeatmapData, EngagementSample, Platform } from '../promo/types';

/** Minimal post shape the reschedule sheet needs (matches the PromoPost view model). */
export interface ReschedulablePost {
  id: string;
  title: string;
  scheduledAt: string | null;
}
import { C, mono, SANS } from '../theme';

// ── Shared bits ──────────────────────────────────────────────────

function Spinner({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: C.faint2, padding: '10px 0' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: C.faint, display: 'inline-block', animation: `briefPulse 1.2s infinite ${d}s` }} />
        ))}
      </div>
      <span style={{ fontSize: 12.5 }}>{label}</span>
    </div>
  );
}

// ── B1: per-post metrics ─────────────────────────────────────────

interface AggMetrics {
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
}

/**
 * Aggregate engagement snapshots into one total: metrics are periodic snapshots,
 * so we take the LATEST snapshot per platform (by fetched_at) and sum across
 * platforms — summing every snapshot would double-count the same post.
 */
export function aggregateMetrics(samples: EngagementSample[]): AggMetrics {
  const latestByPlatform = new Map<string, EngagementSample>();
  for (const s of samples) {
    const prev = latestByPlatform.get(s.platform);
    if (!prev || new Date(s.fetched_at).getTime() > new Date(prev.fetched_at).getTime()) {
      latestByPlatform.set(s.platform, s);
    }
  }
  const agg: AggMetrics = { impressions: 0, likes: 0, comments: 0, shares: 0, clicks: 0 };
  for (const s of latestByPlatform.values()) {
    agg.impressions += s.impressions ?? 0;
    agg.likes += s.likes ?? 0;
    agg.comments += s.comments ?? 0;
    agg.shares += s.shares ?? 0;
    agg.clicks += s.clicks ?? 0;
  }
  return agg;
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: C.ink2 }}>{fmt(value)}</span>
      <span style={mono({ fontSize: 9.5, letterSpacing: '.04em', textTransform: 'uppercase', color: C.faint })}>{label}</span>
    </span>
  );
}

/** Lazily loads and shows one post's engagement. Rendered only when expanded. */
export function PostMetricsPanel({ postId }: { postId: string }) {
  const [state, setState] = useState<{ agg: AggMetrics; empty: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setState(null);
    setError(null);
    postMetrics(postId).then(
      (samples) => {
        if (!alive) return;
        setState({ agg: aggregateMetrics(samples), empty: samples.length === 0 });
      },
      (e) => alive && setError(e instanceof Error ? e.message : 'Не удалось загрузить метрики'),
    );
    return () => {
      alive = false;
    };
  }, [postId]);

  return (
    <div style={{ marginTop: 4, borderTop: `1px solid ${C.line4}`, paddingTop: 10 }}>
      {error ? (
        <div style={{ fontSize: 12.5, color: '#c1442e' }}>{error}</div>
      ) : !state ? (
        <Spinner label="Загружаю метрики…" />
      ) : state.empty ? (
        <div style={{ fontSize: 12.5, color: C.faint }}>Метрик пока нет — соберутся после публикации.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'baseline' }}>
          <Metric label="показы" value={state.agg.impressions} />
          <Metric label="лайки" value={state.agg.likes} />
          <Metric label="коммент." value={state.agg.comments} />
          <Metric label="репосты" value={state.agg.shares} />
          <Metric label="клики" value={state.agg.clicks} />
        </div>
      )}
    </div>
  );
}

// ── B2: reschedule sheet ─────────────────────────────────────────

/** Convert an ISO string to the `YYYY-MM-DDTHH:mm` local value a datetime-local input wants. */
function toLocalInput(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Bottom sheet (mobile-first) to move or clear a post's scheduled time.
 * Uses the native datetime-local picker so mobile gets the OS wheel.
 */
export function RescheduleSheet({
  post,
  onClose,
  onDone,
}: {
  post: ReschedulablePost;
  onClose: () => void;
  onDone: () => void;
}) {
  const [value, setValue] = useState(() => toLocalInput(post.scheduledAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (scheduledAt: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await reschedule(post.id, scheduledAt);
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось перенести');
      setBusy(false);
    }
  };

  const title = post.title || 'Пост';

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(26,25,22,.32)', zIndex: 60 }} />
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          margin: '0 auto',
          width: '100%',
          maxWidth: 440,
          background: C.white,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          boxShadow: '0 -8px 30px rgba(26,25,22,.18)',
          zIndex: 61,
          padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
          animation: 'briefUp .25s ease both',
          fontFamily: SANS,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Перенести публикацию</div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, color: C.faint, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>

        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: '100%', padding: '11px 12px', border: `1px solid ${C.line2}`, borderRadius: 12, fontFamily: SANS, fontSize: 15, color: C.ink, background: C.white, marginBottom: 14, boxSizing: 'border-box' }}
        />

        {error && <div style={{ fontSize: 12.5, color: '#c1442e', marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            disabled={busy || !value}
            onClick={() => submit(new Date(value).toISOString())}
            style={{ flex: 1, background: C.ink, color: C.white, border: 'none', borderRadius: 11, padding: '12px 14px', fontFamily: SANS, fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy || !value ? 0.6 : 1 }}
          >
            {busy ? 'Переношу…' : 'Перенести'}
          </button>
          <button
            disabled={busy}
            onClick={() => submit(null)}
            style={{ background: 'none', border: `1px solid ${C.line3}`, borderRadius: 11, padding: '12px 14px', fontFamily: SANS, fontSize: 14, color: C.sub, cursor: 'pointer' }}
          >
            Убрать дату
          </button>
        </div>
      </div>
    </>
  );
}

// ── B3: best-time heatmap ────────────────────────────────────────

const HEAT_PLATFORMS: Platform[] = ['telegram', 'twitter', 'instagram', 'linkedin'];
const HEAT_PLATFORM_LABEL: Record<string, string> = {
  telegram: 'Telegram',
  twitter: 'X.com',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
};
// Monday-first display order over the worker's 0=Sun..6=Sat cells.
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DOW_LABEL = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/** Light → coral heat colour for a 0..1 score. */
function heatColor(score: number): string {
  const s = Math.max(0, Math.min(1, score));
  if (s <= 0.02) return C.sidebar;
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * s);
  const r = lerp(0xf1, 0xc1);
  const g = lerp(0xee, 0x44);
  const b = lerp(0xe9, 0x2e);
  return `rgb(${r},${g},${b})`;
}

/**
 * Collapsible best-time advisory: dow×hour heat grid per platform. Read-only
 * hint (heatmaps are soft-fetched, empty when there is not enough data yet).
 */
export function BestTimeHeatmap({ mobile }: { mobile?: boolean }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState<Platform>('telegram');
  const [data, setData] = useState<Record<Platform, HeatmapData> | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setData(null);
    bestTime(platform, 30).then((r) => alive && setData(r.data));
    return () => {
      alive = false;
    };
  }, [open, platform]);

  const cells = data?.[platform]?.cells ?? [];
  const grid = new Map<string, number>();
  for (const c of cells) grid.set(`${c.dow}-${c.hour}`, c.score);
  const hasData = cells.length > 0;
  const cell = mobile ? 12 : 15;

  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, background: C.white, marginBottom: 16, overflow: 'hidden' }}>
      <div onClick={() => setOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', cursor: 'pointer' }}>
        <span style={mono({ fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint })}>Лучшее время</span>
        <span style={{ fontSize: 13, color: C.muted }}>когда публиковать</span>
        <span style={{ marginLeft: 'auto', color: C.faint, fontSize: 13 }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <div className="brief-scroll-hidden" style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12 }}>
            {HEAT_PLATFORMS.map((p) => {
              const active = p === platform;
              return (
                <button
                  key={p}
                  onClick={() => setPlatform(p)}
                  style={{ flexShrink: 0, fontFamily: SANS, fontSize: 12.5, borderRadius: 9, padding: '6px 12px', cursor: 'pointer', ...(active ? { background: C.ink, color: C.white, border: `1px solid ${C.ink}`, fontWeight: 600 } : { background: 'transparent', color: C.sub, border: `1px solid ${C.line2}` }) }}
                >
                  {HEAT_PLATFORM_LABEL[p]}
                </button>
              );
            })}
          </div>

          {!data ? (
            <Spinner label="Загружаю…" />
          ) : !hasData ? (
            <div style={{ fontSize: 13, color: C.faint }}>Пока мало данных для рекомендации по времени.</div>
          ) : (
            <div className="brief-scroll-hidden" style={{ overflowX: 'auto' }}>
              <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
                {/* hour axis */}
                <div style={{ display: 'flex', gap: 3, marginLeft: 26 }}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} style={mono({ width: cell, textAlign: 'center', fontSize: 7.5, color: C.faint })}>
                      {h % 3 === 0 ? h : ''}
                    </div>
                  ))}
                </div>
                {DOW_ORDER.map((dow) => (
                  <div key={dow} style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <div style={mono({ width: 23, fontSize: 9, color: C.muted })}>{DOW_LABEL[dow]}</div>
                    {Array.from({ length: 24 }, (_, h) => {
                      const score = grid.get(`${dow}-${h}`) ?? 0;
                      return (
                        <div
                          key={h}
                          title={`${DOW_LABEL[dow]} ${String(h).padStart(2, '0')}:00 (UTC) · ${Math.round(score * 100)}%`}
                          style={{ width: cell, height: cell, borderRadius: 3, background: heatColor(score) }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
              <div style={mono({ fontSize: 9.5, color: C.faint, marginTop: 8 })}>Время в UTC · темнее = выше вовлечённость</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
